import { Loading, Icon, Popover } from '@brighterion/br-ui';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Table from 'Components/Table';
import useStickyTableHead from 'Hooks/useStickyTableHead';
import useGetRuleQuickFilters from 'Hooks/rule-quickfilters';
import { SIMULATION_STATUS } from 'Common/enums';
import { getUser, isDeleteSimulationModalOpen, shouldShowMerchants } from 'Selectors';
import { useSelector } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { cloneDeep, get } from 'lodash';
import { Column, Row } from 'react-table';
import { RoleService, getSimulationById } from 'Services';
import { RowActionDropdown } from 'Components/Table/RowActionDropdown';
import useSingleReportCSV from 'Hooks/query-hooks/simulations/useSingleReportCSV';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';

import { RQ_ALL_SIMULATIONS } from 'Utils';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import DeleteSimulationModal from '../DeleteSimulationModal/DeleteSimulationModal';
import styles from './SimulationsTable.css';

const TABLE_ID = 'SIMULATIONS-TABLE';

type Props = {
    rawData: Simulation[];
    rawColumns: Column<Simulation>[];
    hideKebabMenuCol?: boolean;
    hideCompareCol?: boolean;
};

const SimulationsTable = ({ rawData, rawColumns, hideKebabMenuCol, hideCompareCol }: Props) => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { path } = useRouteMatch();
    useStickyTableHead(TABLE_ID);
    const { addTechnicalErrorModal } = useReduxModal();

    const [rowValues, setRowValues] = useState({
        id: -1,
        createdAt: '',
        createdBy: '',
        start: '',
        scope: '',
        sandbox: '',
        simulationType: '',
        duration: '',
        compare: false
    });
    const [deleteSimulationIsOpen, setDeleteSimulationIsOpen] = useState(false);

    const { userId } = useSelector(getUser);
    const deleteSimulationModalOpen = useSelector(isDeleteSimulationModalOpen);
    const { fetchAccessToken } = useAccessTokenStore.getState();

    const columns = React.useMemo<Column<Simulation>[]>(() => {
        const dropdownCol: Column<Simulation> = {
            accessor: 'id',
            id: 'take-action-on-simulation',
            disableSortBy: true,
            width: 40,
            maxWidth: 40,
            renderType: 'overrideCell',
            Cell: ({ value: id, row }: { value: number; row: Row }) => {
                const startDate = new Date(row.original.start);
                const dateString = `${startDate.toLocaleString('default', {
                    month: 'short'
                })} ${startDate.getDate()} ${startDate.getFullYear()}`;
                const fileName =
                    row?.original?.compareFlag === 'Yes'
                        ? `Compare_Simulation_Rule-Performance_${dateString}_All-Rules_24_Hours`
                        : `Simulation_Rule-Performance_${dateString}_All-Rules_24_Hours`;
                const csv = useSingleReportCSV(id, fileName);
                const isRowDisabled = get(row, 'original.status') !== SIMULATION_STATUS.FINISHED;
                const menuActions: any = [
                    {
                        testId: 'delete-simulation',
                        label: t('Delete'),
                        handleAction: (event: React.MouseEvent) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setDeleteSimulationIsOpen(true);
                            setRowValues({
                                id: row?.original.id,
                                createdAt: row?.original?.createdAt,
                                createdBy: row?.original?.createdBy,
                                start: row?.original?.start,
                                scope: shouldShowMerchants() ? t('All Merchants') : '',
                                sandbox: row?.original?.sandbox,
                                simulationType: row?.original?.simulationType,
                                duration: row?.original?._formatted.duration,
                                compare: !!row?.original?.compare?.length
                            });
                        },

                        style: { color: 'var(--red000)' }
                    }
                ];
                if (!isRowDisabled) {
                    menuActions.unshift({
                        testId: 'export-csv-simulation',
                        label: t('Export CSV'),
                        handleAction: (event: React.MouseEvent) => {
                            event.preventDefault();
                            event.stopPropagation();
                            csv.refetch();
                            trackEvent({
                                eventType: EventTypes.track,
                                eventName: EventNames.click,
                                payload: {
                                    clickIdentifier: 'Manage Simulations - Export CSV',
                                    text: 'Simulations - Export CSV'
                                }
                            });
                        }
                    });
                }
                return (
                    <>
                        {csv.isInitialLoading ? (
                            <div style={{ marginLeft: '-8px' }}>
                                <Loading size='sm' fillName='orange' />
                            </div>
                        ) : (
                            <RowActionDropdown menuId={`${id}-action-dropdown`} menuActions={menuActions} />
                        )}
                    </>
                );
            }
        };

        const statusIconCol: Column<Simulation> = {
            accessor: 'id',
            id: 'status-id',
            disableSortBy: true,
            width: 32,
            maxWidth: 32,
            renderType: 'overrideCell',
            Cell: ({ row }: { row: Row }) => {
                const hasRoleAccess = RoleService.hasDesignAccess;
                const { status = 'default' } = row?.original;

                if (hasRoleAccess) {
                    const simStatus = {
                        [SIMULATION_STATUS.DEPLOYMENT_FAILED]: 'FAILED',
                        [SIMULATION_STATUS.FAILED]: 'FAILED',
                        [SIMULATION_STATUS.FINISHED]: 'FINISHED',
                        [SIMULATION_STATUS.PROCESSING]: 'IN_PROCESS',
                        [SIMULATION_STATUS.QUEUED]: 'IN_PROCESS',
                        [SIMULATION_STATUS.DEPLOYING]: 'IN_PROCESS',
                        default: ''
                    } as any;

                    const iconProp = {
                        FINISHED: {
                            size: 'sm',
                            'data-testid': 'table-new'
                        },
                        IN_PROCESS: {
                            stroke: '#F38B00',
                            size: 'md',
                            className: styles.statusTooltipIcon,
                            'data-testid': 'in-progress'
                        },
                        FAILED: {
                            stroke: '#ffffff',
                            size: 'md',
                            className: styles.statusErrorIcon,
                            'data-testid': 'small-error'
                        }
                    } as any;

                    const popoverProp = {
                        FAILED: {
                            content: t('Simulation could not be completed due to an error'),
                            position: 'right'
                        },
                        IN_PROCESS: {
                            content: t('Simulation report is in progress'),
                            position: 'right'
                        },
                        FINISHED: {
                            showOnHover: false
                        }
                    } as any;

                    const iconName = {
                        FAILED: 'small-error',
                        IN_PROCESS: 'in-progress',
                        FINISHED: row?.original.isNewSimulation && 'table-new'
                    } as any;

                    return (
                        <Popover
                            showOnHover
                            {...popoverProp[simStatus[status]]}
                            showArrow
                            position='top-start'
                            contentClassName={styles.popOverContentStyle}
                            arrowClassName={styles.popperArrowStyle}
                        >
                            <div>
                                <Icon name={iconName[simStatus[status]]} {...iconProp[simStatus[status]]} />
                            </div>
                        </Popover>
                    );
                }
                return null;
            }
        };

        let cols = [statusIconCol, ...rawColumns];

        if (!hideKebabMenuCol) {
            cols = cols.concat(dropdownCol);
        }
        if (hideCompareCol) {
            cols = cols.filter((col) => col.accessor !== 'compareFlag');
        }

        return cols;
    }, [rawColumns, hideKebabMenuCol, hideCompareCol, t]);

    const data = useMemo(() => {
        const tempData = cloneDeep(rawData);
        const allRulesString = t('All Rules');
        tempData.forEach((simulation) => {
            if (simulation.sandbox === 'ALL') {
                simulation.sandbox = allRulesString;
            }
        });
        return tempData;
    }, [rawData, t]);

    const quickFilters = useGetRuleQuickFilters({
        quickFilters: ['newSimulations', 'simulationsInProgress', 'mySimulations'],
        qfOverrides: [
            {
                id: 'mySimulations',
                data: [
                    {
                        id: 'mySimulations',
                        text: t('My Simulations'),
                        options: {
                            conditional: 'eq',
                            valueToMatch: userId,
                            fieldToMatch: 'createdBy'
                        }
                    }
                ]
            },
            {
                id: 'simulationsInProgress',
                data: [
                    {
                        id: 'simulationsInProgress',
                        text: t('In Progress'),
                        options: {
                            conditional: 'arrayIncludes',
                            valueToMatch: [
                                SIMULATION_STATUS.QUEUED,
                                SIMULATION_STATUS.DEPLOYING,
                                SIMULATION_STATUS.PROCESSING
                            ],
                            fieldToMatch: 'status',
                            iconName: 'in-progress'
                        }
                    }
                ]
            },
            {
                id: 'newSimulations',
                data: [
                    {
                        id: 'newSimulations',
                        text: t('New'),
                        options: {
                            conditional: 'eq',
                            valueToMatch: true,
                            fieldToMatch: '_formatted.isNewSimulation',
                            iconName: 'table-new'
                        }
                    }
                ]
            }
        ]
    });

    const handleRowClick = async (
        row: Row<Simulation>,
        e: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>
    ) => {
        const targetAsAny = e.target as any; // do this so that we can do things like contains
        if (
            e.currentTarget.contains(targetAsAny) &&
            // hack to see if the target is something we can interact with
            !targetAsAny?.getAttribute?.('data-ignore-on-row-interact') &&
            row.original.status === SIMULATION_STATUS.FINISHED
        ) {
            try {
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: 'Manage Simulations - View Simulation',
                        text: 'View Simulation'
                    }
                });
                await fetchAccessToken();
                await getSimulationById(row?.original.id);
                history.push(`${path}/${row?.original.id}`);
                queryClient.invalidateQueries([RQ_ALL_SIMULATIONS]);
            } catch (error) {
                const content = t('alreadyDeletedSimulationAccessed');
                addTechnicalErrorModal(
                    '',
                    t('Simulation is already deleted'),
                    () => {
                        queryClient.invalidateQueries([RQ_ALL_SIMULATIONS]);
                        history.push('/simulations');
                    },
                    {
                        status: 'warning',
                        content
                    }
                );
            }
        }
    };

    return (
        <div className={styles.simulationTable}>
            <Table
                tableName={TABLE_ID}
                dataTestId={TABLE_ID}
                showExpandableSearch
                showRichFilter
                showMultiSort
                clickableRow
                enablePagination
                initialState={{
                    pageSize: 20,
                    sortBy: [{ id: '_formatted.createdAt', desc: true }],
                    globalFilter: {
                        quickFilters: quickFilters.initialQuickFilters
                    }
                }}
                noDataMessage='noSimulationData'
                columns={columns}
                data={data}
                quickFilters={quickFilters.filters}
                inactiveRowProps={{
                    inactiveRowFn: (row) =>
                        get(row, 'original.status') !== SIMULATION_STATUS.FINISHED && !deleteSimulationModalOpen
                }}
                onRowInteractCB={(row, e) => handleRowClick(row, e)}
            />
            <DeleteSimulationModal
                isOpen={deleteSimulationIsOpen}
                onClose={() => setDeleteSimulationIsOpen(false)}
                rowValues={rowValues}
            />
        </div>
    );
};

export default React.memo(SimulationsTable);
